import {ChangeDetectionStrategy, Component, inject, input, output} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {ProductPackagesModalService} from "../../services/product-packages-modal.service";
import {ProductPackagesSelectDataStateService} from "../../services/product-packages-select-data-state.service";
import {
  BaseProductPackageData,
  ProductPackageData,
  ProductPackageFormField
} from "../../types/product-packages-modal.types";
import {productPackageFormFields} from "../../configs/product-package-form-fields";
import {defaultProductPackageData} from "../../configs/default-product-package";
import {RecordUpdateRequestParams, SelectData} from "../../../../../../../core/types/records-table.types";
import {productPackageResourceType} from "../../configs/product-packages-modal-config";
import {productPackagesFormComponents} from "../../configs/product-packages-row-keys";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {
  CustomMatSelectComponent
} from "../../../../../../../shared/components/ui/custom-mat-select/custom-mat-select.component";
import {DecimalLimitDirective} from "../../../../../../../shared/directives/decimal-limit.directive";
import {MatButton} from "@angular/material/button";
import {MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";

@Component({
  selector: 'app-product-packages-form',
  imports: [
    CdkTextareaAutosize,
    CustomMatSelectComponent,
    DecimalLimitDirective,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    ReactiveFormsModule
  ],
  templateUrl: './product-packages-form.component.html',
  styleUrls: [
    './product-packages-form.component.scss',
    '../../product-packages-modal.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductPackagesFormComponent {
  parentId = input<number>(0);
  productPackageFormGroup = input<FormGroup>(new FormGroup({}));
  productPackageData = input<ProductPackageData>(defaultProductPackageData);
  isEditModeActive = input<boolean>(false);
  noFirstData = input<boolean>(false);
  modalKey = input<string>('');
  updateProductPackageData = output<ProductPackageData>();
  deleteProductPackage = output<void>();

  private productPackagesModalService: ProductPackagesModalService = inject(ProductPackagesModalService);
  private productPackagesSelectDataStateService: ProductPackagesSelectDataStateService = inject(ProductPackagesSelectDataStateService);

  public readonly productPackagesFormComponents = productPackagesFormComponents;

  public getRowClass(row: string): string {
    const rowClasses: Record<string, string> = {
      size: 'row-size',
      articles: 'row-articles',
    };

    return rowClasses[row] || '';
  }

  public getFormFields(rowKey: string): Array<ProductPackageFormField> {
    if (rowKey === 'palletType' && this.modalKey() !== 'palletModalDto') { return []; }

    return productPackageFormFields.filter(i => i.rowKey === rowKey);
  }

  public getFieldClass(): string {
    if (!this.productPackageData().id) { return ''; }

    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public getEditModeForSelect(key?: string): boolean {
    if (key === 'packageType') { return false; }
    if (key && !this.getSelectData(key).length) { return false; }
    if (!this.productPackageData().id) { return true; }

    return this.isEditModeActive();
  }

  public async handleValueChange(key: string, value?: string): Promise<void> {
    if (!this.productPackageData().id) { return; }
    if (this.productPackageFormGroup().get(key)?.invalid) {
      this.productPackageFormGroup().get(key)?.setValue(this.productPackageData()[key])
      return;
    }

    value && this.productPackageFormGroup().get(key)?.setValue(value);
    await this.updateFormField(key);
  }

  public async handleSelectChange(value: string, key: string): Promise<void> {
    this.productPackageFormGroup().get(key)?.setValue(value);
    const name = this.productPackageFormGroup().get('packageName' as string)?.value;
    if (!name && value) {
      const valueDisplay = this.productPackagesSelectDataStateService.packageTypeSelectData().find(i => String(i.id) === value)?.value;
      this.productPackageFormGroup().get('packageName' as string)?.setValue(valueDisplay);
    }

    if (!this.productPackageData().id) { return; }

    await this.updateFormField(key);
  }

  public handelDelete(): void {
    this.deleteProductPackage.emit();
  }

  public async handelCreate(): Promise<void> {
    const createData: BaseProductPackageData = {
      technicalParametersId: this.parentId(),
      ...this.productPackageFormGroup().value
    };

    const data = await this.productPackagesModalService.createProductPackage(createData)
      .catch(() => defaultProductPackageData);
    this.updateProductPackageData.emit(data);
  }

  public getSelectData(key: string): Array<SelectData> {
    return this.productPackagesSelectDataStateService.getSelectDataByKey(key);
  }

  public getSelectedValue(key: string): string {
    return String(this.productPackageFormGroup().get(key)?.value) || '';
  }

  private async updateFormField(key: string): Promise<void> {
    const params: RecordUpdateRequestParams = {
      path: productPackageResourceType.path,
      recordId: this.productPackageData().id,
      fieldName: key,
      value: this.productPackageFormGroup().get(key)?.value
    };

    const data = await this.productPackagesModalService.updatedRecordField<ProductPackageData>(params)
      .catch(() => this.productPackageData());
    this.updateProductPackageData.emit(data);
  }
}
